import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { LoyaltyProgramFacade } from '@features/loyalty-program/facades/loyalty-program.facade';
import { OfferFacade } from '@features/offer/facades';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CmsFacade } from '@rd/cms/facades/cms.facade';
import { AuthFacade } from '@rd/core/auth/store/facades/auth.facade';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { CORE_AUTH_SECURE_ROUTE_DIALOG } from '@rd/core/tokens/popup.token';
import { TRAVEL_EDGE_BASE_URL } from '@rd/core/tokens/rd-lib.token';
import { CampaignBanner, MultipleCampaignBanner } from '@rd/types';
import { getElementBy } from '@rd/utils/getElementBy';
import { isNotNull } from '@rd/utils/isNotNull';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import {
  BNZ_BANKWIDE_UPDATES_ENABLED,
  BNZ_BANK_WIDE_ENABLED,
  BNZ_DEPRECATE_FLYBUYS_SCHEME_ENABLED,
  BNZ_VOX_ENABLED,
} from '../../../core/tokens/features.tokens';
import { RewardsProgramTransferConnectIdentifier } from '../../../rewards-program/types';
import { QuickLinkInput } from '../../components/homepage-quick-link/homepage-quick-link.component';

@UntilDestroy()
@Component({
  selector: 'bnz-home-page',
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BnzHomePageComponent implements OnInit {
  private authFacade = inject(AuthFacade);
  private offerFacade = inject(OfferFacade);
  private cmsFacade = inject(CmsFacade);
  private travelEdgeBaseUrl = inject(TRAVEL_EDGE_BASE_URL);

  isBankwideEnabled = inject(BNZ_BANK_WIDE_ENABLED);
  isDeprecateFlybuysSchemeEnabled: boolean = inject(
    BNZ_DEPRECATE_FLYBUYS_SCHEME_ENABLED,
  );
  isBnzBankwideUpdatesEnabled = inject(BNZ_BANKWIDE_UPDATES_ENABLED);
  isVoxEnabled$ = inject(BNZ_VOX_ENABLED);

  coreAuthSecureRouteDialogComponentType = inject(
    CORE_AUTH_SECURE_ROUTE_DIALOG,
  );

  homepagePromotions$: Observable<
    Omit<CampaignBanner, 'name' | 'position' | 'type' | 'skuList'>[]
  > = this.cmsFacade
    .getContentByName({
      pageName: 'homepage-promotions',
      pageType: 'campaignbannermultiple',
      locale: 'en-AU',
    })
    .pipe(
      filter(isNotNull),
      map((campaignBanner) => {
        if (campaignBanner) {
          return (campaignBanner as MultipleCampaignBanner).items.map((x) => {
            return {
              ...x,
              link: x.link.includes(this.travelEdgeBaseUrl)
                ? this.urlUtilsService
                    .travelEdgeUrl(new URL(x.link).pathname)
                    .toString()
                : x.link,
            };
          });
        } else {
          return [];
        }
      }),
    );

  isLoggedIn$: Observable<boolean> = this.authFacade.isLoggedIn$;

  travelEdgeUrl: URL = this.urlUtilsService.travelEdgeUrl('');

  flyBuysLoyaltyProgramId$: Observable<string> =
    this.loyaltyProgramFacade.loyaltyPrograms$.pipe(
      getElementBy(
        (lp) =>
          lp.transferConnectIdentifier ===
          RewardsProgramTransferConnectIdentifier.Flybuys,
      ),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      map((lp) => lp?.id!),
    );

  private voxFlagResolved$ = new BehaviorSubject(false);

  readonly offersLoading$ = this.offerFacade.offerLoading$.pipe(
    distinctUntilChanged(),
  );

  readonly showOffersLoading$ = combineLatest([
    this.offersLoading$,
    this.voxFlagResolved$,
  ]).pipe(
    map(([loading, voxResolved]) => !voxResolved || loading),
    distinctUntilChanged(),
  );
  readonly offers$ = this.isVoxEnabled$.pipe(
    switchMap((isVoxEnabled) =>
      isVoxEnabled
        ? this.offerFacade.getOffersBySource$('points_boost').pipe(
            switchMap((pointsBoostOffers) =>
              pointsBoostOffers.length
                ? of(pointsBoostOffers)
                : this.offerFacade.getOffersBySource$('partner'),
            ),
            distinctUntilChanged(),
          )
        : this.offerFacade.getOffersBySource$('partner'),
    ),
    distinctUntilChanged(),
  );

  quickLinks: QuickLinkInput[] = this.isBnzBankwideUpdatesEnabled
    ? [
        {
          iconSymbol: 'icon-my-perks',
          path: '/offers',
          text: 'homepage.bnz_perks',
        },
        {
          iconSymbol: 'icon-home-page-cashback',
          path: '/redeem_cash',
          text: 'homepage.cashback_update',
        },
        {
          iconSymbol: 'icon-home-page-travel',
          path: `${this.travelEdgeUrl.toString()}flights`,
          text: 'homepage.book_travel',
        },
        {
          iconSymbol: 'icon-home-page-giftcards',
          path: '/gift_card',
          text: 'homepage.gift_cards',
        },
        {
          iconSymbol: 'icon-home-page-points-transfer',
          path: '/transfer',
          text: 'homepage.transfer_miles',
        },
      ]
    : [
        {
          iconSymbol: 'icon-home-page-travel',
          path: `${this.travelEdgeUrl.toString()}flights`,
          text: 'homepage.book_travel',
        },
        {
          iconSymbol: 'icon-home-page-giftcards',
          path: '/gift_card',
          text: 'homepage.gift_cards',
        },
        {
          iconSymbol: 'icon-home-page-cashback',
          path: '/redeem_cash',
          text: 'homepage.cashback_update',
        },
        {
          iconSymbol: 'icon-home-page-points-transfer',
          path: '/transfer',
          text: 'homepage.transfer_miles',
        },
      ];

  constructor(
    public urlUtilsService: UrlUtilsService,
    private loyaltyProgramFacade: LoyaltyProgramFacade,
    private title: Title,
  ) {
    title.setTitle('Home | BNZ Rewards Portal');
  }

  ngOnInit(): void {
    combineLatest([this.isVoxEnabled$, this.isLoggedIn$])
      .pipe(
        distinctUntilChanged(
          (prev, curr) => prev[0] === curr[0] && prev[1] === curr[1],
        ),
        untilDestroyed(this),
      )
      .subscribe(([isVoxEnabled, isLoggedIn]) => {
        this.voxFlagResolved$.next(true);
        if (isVoxEnabled && isLoggedIn) {
          this.offerFacade.loadBnzHomepageOffers({ isVoxEnabled });
        } else {
          this.offerFacade.loadPaginatedOffers({
            queryParams: { sort: '-score_by_recommended', source: 'partner' },
            paginationOptions: { page: 1, pageSize: 3 },
          });
        }
      });
  }
}
